import { Flex,Button,Box,Image, Text, Heading, Grid, GridItem } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useRef, useState, useEffect } from 'react';
import { MdOutlineFileDownload as DownloadIcon } from "react-icons/md";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useQuery, useQueryClient } from 'react-query';
import apiCall from '../../../../utils/apiCall';
import { Loader } from '../../../../customs';
import { TranscriptAmount } from '../../Settings';
import { useAtom } from 'jotai';
import { useMemo } from 'react';
import React from 'react'; 
import { openAtom,modalAtom,notificationAtom,openNAtom } from '../../../../context';


type TranscriptDataType = {
  Id: number;
  Code: string;
  Body: string;
};

const daysOfWeek = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const monthOfYear = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const Transcript = (props: any) => {
  const pdfRef = useRef<any>(null);
  const StudentNo = props?.StudentNo;
  const Id = props?.Id;
  const queryClient = useQueryClient();
  const [, set_open] = useAtom(openAtom);
  const [, set_modal] = useAtom(modalAtom);
  const [, set_n_open] = useAtom(openNAtom);
  const [, set_notification] = useAtom(notificationAtom);

  // ✅ Fetch transcript details (student data)
  const { isLoading, data: transcript_detail } = useQuery(
    ['transcript-details', StudentNo, Id],
    {
      queryFn: async () =>
        await apiCall().post(
          `/Exam/transcriptTemplateDetails?studentNo=${StudentNo}&id=${Id}`
        ),
      select(data) {
        return data.data.Data;
      },
      onError(err: any) {
        set_open(false);
        set_notification({
          type: 'error',
          message:
            err?.response?.data?.Message ||
            'This transcript cannot be generated, its either the result has not been completed or not processed',
        });
        set_n_open(true);
      },
    }
  );

  // ✅ Fetch transcript template from API
  const {
    isLoading: templateLoading,
    data: transcriptTemplate,
    refetch: refetchTemplate,
  } = useQuery<TranscriptDataType | null>({
    queryKey: ['transcript-template'],
    queryFn: async () => {
      const res = await apiCall().get(
        '/exam/setup/exam/GetExamTranscriptTemplate'
      );
      return res?.data?.Data ?? null;
    },
    // ✅ Add staleTime to force refetch after updates
    staleTime: 5000,
  });

  // ✅ Force refetch template when component mounts
  useEffect(() => {
    refetchTemplate();
  }, [refetchTemplate]);

  // ✅ Process template: Replace placeholders with actual data
  const processedHtml = React.useMemo(() => {
    if (!transcriptTemplate?.Body || !transcript_detail) return null;

    let html = transcriptTemplate.Body;

    // 🔹 Replace simple placeholders
    const replacements: Record<string, string> = {
      '{{StudentName}}': transcript_detail?.StudentName || '',
      '{{DestinationAddress}}': transcript_detail?.DestinationAddress || '',
      '{{StudentAdmissionYear}}': new Date(
        transcript_detail?.StudentAdmissionDate
      )?.getFullYear().toString() || '',
      '{{StudentGraduationYear}}': new Date(
        transcript_detail?.StudentGraduationDate
      )?.getFullYear().toString() || '',
    };

    // Replace date placeholders
    const genDate = new Date(transcript_detail?.TransciptGenerationDate);
    replacements['{{TransciptGenerationDateDay}}'] = genDate?.getDate().toString() || '';
    replacements['{{TransciptGenerationDateMonth}}'] =
      monthOfYear[genDate?.getMonth()] || '';
    replacements['{{TransciptGenerationDateYear}}'] =
      genDate?.getFullYear().toString() || '';

    // Replace all simple placeholders
    Object.keys(replacements).forEach((key) => {
      const regex = new RegExp(key, 'g');
      html = html.replace(regex, replacements[key]);
    });

    // 🔹 Generate dynamic table for Levels and Subjects
    if (transcript_detail?.Levels && transcript_detail.Levels.length > 0) {
      const tableHtml = transcript_detail.Levels
        .map((eLevel: any) => {
          const levelHtml = `
            <div>
              <div style="display: flex; margin: 20px 0 10px; font-size: 14px; justify-content: space-between;">
                <span style="font-size: 16px;">${eLevel.LevelName || ''}</span>
                <span style="width: 150px; font-size: 16px;">GRADE</span>
              </div>
              ${
                eLevel.Subjects && eLevel.Subjects.length > 0
                  ? eLevel.Subjects
                      .map((eSubject: any) => `
                        <div style="display: flex; justify-content: space-between; gap: 20px;">
                          <span>${eSubject?.SubjectName || ''}</span>
                          <span style="margin-left: auto;">-</span>
                          <span style="font-size: 16px; width: 150px;">${eSubject.Grade || ''}</span>
                        </div>
                      `)
                      .join('')
                  : ''
              }
            </div>
          `;
          return levelHtml;
        })
        .join('');

      const tableBlock = `<div style="display: grid;">${tableHtml}</div>`;

      // Support the visible {{TranscriptTable}} placeholder (survives WYSIWYG
      // editing) as well as the legacy HTML-comment marker from older templates.
      const placeholderRegex = /\{\{\s*TranscriptTable\s*\}\}|<!--\s*TABLE_PLACEHOLDER\s*-->/;
      if (placeholderRegex.test(html)) {
        html = html.replace(placeholderRegex, tableBlock);
      } else {
        // Placeholder missing from the template body - append the table rather
        // than silently dropping it from the generated transcript.
        html += tableBlock;
      }
    }

    return html;
  }, [transcriptTemplate, transcript_detail]);

  // ✅ Download PDF function
  const downloadPdf = () => {
    const input = pdfRef.current;
    if (pdfRef && pdfRef.current) {
      html2canvas(input).then(function (canvas) {
        var imgWidth = 210;
        var pageHeight = 290;
        var imgHeight = (canvas.height * imgWidth) / canvas.width;
        var heightLeft = imgHeight;

        var doc = new jsPDF('p', 'mm');
        var position = 0;
        var pageData = canvas.toDataURL('image/jpeg', 1.0);
        var imgData = encodeURIComponent(pageData);
        doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        doc.setLineWidth(5);
        doc.setDrawColor(255, 255, 255);
        doc.rect(0, 0, 210, 295);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          doc.addPage();
          doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          doc.setLineWidth(5);
          doc.setDrawColor(255, 255, 255);
          doc.rect(0, 0, 210, 295);
          heightLeft -= pageHeight;
        }
        doc.save('transcript.pdf');
      });
    }
  };

  // ✅ Loading state
  if (isLoading || templateLoading) {
    return <Loader />;
  }

  // ✅ No data state
  if (!transcript_detail || !processedHtml) {
    return (
      <Wrapper>
        <Flex justify="flex-end" mb="30px">
          <Button
            _hover={{ bg: 'var(--dark-green-color)' }}
            onClick={downloadPdf}
            bg="var(--dark-green-color)"
            color="var(--white-color)"
            leftIcon={<DownloadIcon />}
          >
            Download Transcript
          </Button>
        </Flex>
        <Text>No transcript data available</Text>
      </Wrapper>
    );
  }

  return (
    <>
      <Wrapper>
        <Flex justify="flex-end" mb="30px">
          <Button
            _hover={{ bg: 'var(--dark-green-color)' }}
            onClick={downloadPdf}
            bg="var(--dark-green-color)"
            color="var(--white-color)"
            leftIcon={<DownloadIcon />}
          >
            Download Transcript
          </Button>
        </Flex>

        {/* ✅ Use processed HTML from template */}
        <Box sx={{ p: '20px 40px' }} ref={pdfRef}>
          <Box minH="295mm">
            {/* Render the processed HTML */}
            <div dangerouslySetInnerHTML={{ __html: processedHtml }} />
          </Box>
        </Box>
      </Wrapper>
    </>
  );
};

export default Transcript;



const Wrapper = styled.div`
    width:85vw;
`